import React, { useEffect, useRef } from "react"
import { IntlConfig, IntlProvider } from "react-intl"
import { useNavigate } from "react-router-dom"
import { useIdleTimer } from "react-idle-timer"
import { toast, ToastContainer } from "react-toastify"
import { ErrorBoundary } from "react-error-boundary"
import { useQueryErrorResetBoundary } from "@tanstack/react-query"

import AppLocale from "./lang"
import AuthHelper from "./helpers/AuthHelper"
import Routes from "./router/Routes"
import withAuth, { AuthProps } from "./redux/hocs/withAuth"
import ErrorFallback from "./views/errorFallback"
import { EXPIRATION_TIME_90_MINUTES_IN_MS } from "./constants/defaultValues"

const App: React.FC<AuthProps> = ({ logout, userStatusLoading, getUserStatus }) => {
  const navigate = useNavigate()
  const { reset } = useQueryErrorResetBoundary()
  const getNavigatorLanguage = () => {
    return navigator.language.split("-")[0]
  }

  const currentAppLocale: IntlConfig = AppLocale[getNavigatorLanguage()]
  const lastFetchedUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    const id: string | null = AuthHelper.getUserAuth().userId
    if (!id) {
      lastFetchedUserIdRef.current = null
      return
    }
    // Don't fetch user status if MFA is required but not verified - this prevents redirect during MFA flow
    if (AuthHelper.needsToCompleteMfa()) {
      return
    }
    // Only fetch if user ID changed or we haven't fetched yet, and not currently loading
    if (id !== lastFetchedUserIdRef.current && !userStatusLoading) {
      lastFetchedUserIdRef.current = id
      getUserStatus(id, navigate)
    }
  }, [getUserStatus, navigate])

  const handleOnIdle = () => {
    if (AuthHelper.isLoggedIn()) {
      logout(navigate)
      window.location.reload()
    }
  }

  useIdleTimer({
    timeout: EXPIRATION_TIME_90_MINUTES_IN_MS,
    onIdle: handleOnIdle,
    debounce: 500,
  })

  return (
    <div className='h-100' data-testid='app-holder'>
      {/* @ts-expect-error - react-intl v3 IntlProvider doesn't include children in type definition for React 19 */}
      <IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
        <ErrorBoundary FallbackComponent={ErrorFallback.Component} onReset={ErrorFallback.onReset(reset)}>
          <Routes />
        </ErrorBoundary>
      </IntlProvider>
      <ToastContainer position='top-right' />
    </div>
  )
}

export default withAuth(App)
